import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellRing, Clock, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { ActionItemDto } from '@shared/types';
import { Header } from '@/components/Header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { actionItemsApi } from '@/api/action-items';

const STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Активно',
  OVERDUE: 'Просрочено',
  SNOOZED: 'Отложено',
  DISMISSED: 'Скрыто',
  RESOLVED: 'Решено',
};

const SEVERITY_LABELS: Record<string, string> = {
  INFO: 'Инфо',
  WARNING: 'Важно',
  ERROR: 'Ошибка',
  CRITICAL: 'Критично',
};

function formatDate(value?: string | null) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(value));
}

function statusClass(status: string) {
  if (status === 'OVERDUE') return 'border-red-200 bg-red-50 text-red-700';
  if (status === 'RESOLVED') return 'border-green-200 bg-green-50 text-green-700';
  if (status === 'SNOOZED') return 'border-amber-200 bg-amber-50 text-amber-700';
  return 'border-blue-200 bg-blue-50 text-blue-700';
}

function mobileActionUrl(item: ActionItemDto): string | null {
  const payload =
    item.payload && typeof item.payload === 'object' && !Array.isArray(item.payload)
      ? (item.payload as Record<string, unknown>)
      : {};
  const explicit = payload.mobileActionUrl;
  if (typeof explicit === 'string' && explicit.trim()) {
    return explicit;
  }
  if (item.actionUrl?.startsWith('/fuel-accounting')) {
    return '/more/fuel';
  }
  if (item.actionUrl?.startsWith('/document-lookup') && item.sourceModule === 'controlled_documents') {
    const barcode = typeof payload.barcode === 'string' ? payload.barcode : '';
    return barcode ? `/more/controlled-documents?search=${encodeURIComponent(barcode)}` : '/more/controlled-documents';
  }
  return item.actionUrl;
}

function ActionItemCard({
  item,
  onOpen,
}: {
  item: ActionItemDto;
  onOpen: (item: ActionItemDto) => void;
}) {
  return (
    <article
      className="rounded-lg border border-border bg-card p-3 active:bg-accent"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(item)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(item);
        }
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-medium leading-snug">{item.title}</h2>
          <p className="mt-1 line-clamp-3 text-sm text-muted-foreground">{item.summary}</p>
        </div>
        <BellRing className={cn('mt-0.5 h-5 w-5 shrink-0', item.status === 'OVERDUE' ? 'text-red-600' : 'text-amber-600')} />
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Badge variant="outline" className={statusClass(item.status)}>
          {STATUS_LABELS[item.status] || item.status}
        </Badge>
        <Badge variant="outline">{SEVERITY_LABELS[item.severity] || item.severity}</Badge>
        <Badge variant="outline">{item.sourceModule}</Badge>
      </div>
      <div className="mt-3 text-xs text-muted-foreground">Срок: {formatDate(item.dueAt)}</div>
    </article>
  );
}

function ActionItemSheet({
  itemId,
  open,
  onOpenChange,
}: {
  itemId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['mobile-action-item-detail', itemId],
    queryFn: () => actionItemsApi.detail(itemId!),
    enabled: open && Boolean(itemId),
  });

  const snoozeMutation = useMutation({
    mutationFn: () => {
      const until = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
      return actionItemsApi.snooze(itemId!, until, 'Отложено на сутки');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mobile-action-items'] });
      queryClient.invalidateQueries({ queryKey: ['mobile-action-item-detail', itemId] });
      toast.success('Отложено');
    },
  });

  const item = data?.item;
  const actionUrl = item ? mobileActionUrl(item) : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[88vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{item?.title || 'Требует внимания'}</SheetTitle>
          <SheetDescription>{item?.summary || ''}</SheetDescription>
        </SheetHeader>

        {isLoading && (
          <div className="py-10 text-center">
            <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {item && (
          <div className="mt-4 space-y-4">
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className={statusClass(item.status)}>
                {STATUS_LABELS[item.status] || item.status}
              </Badge>
              <Badge variant="outline">{SEVERITY_LABELS[item.severity] || item.severity}</Badge>
              <Badge variant="outline">{item.sourceModule}</Badge>
            </div>

            {item.details && <div className="text-sm text-muted-foreground">{item.details}</div>}

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Срок</div>
                <div>{formatDate(item.dueAt)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Изменено</div>
                <div>{formatDate(item.lastChangedAt)}</div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {actionUrl && (
                <Button asChild size="sm">
                  <a href={actionUrl}>
                    <ExternalLink className="h-4 w-4" />
                    Открыть
                  </a>
                </Button>
              )}
              {item.status !== 'RESOLVED' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => snoozeMutation.mutate()}
                  disabled={snoozeMutation.isPending}
                >
                  <Clock className="h-4 w-4" />
                  Отложить
                </Button>
              )}
            </div>

            <div>
              <h3 className="mb-2 text-sm font-medium">История</h3>
              <div className="space-y-2">
                {data.events.map((event) => (
                  <div key={event.id} className="rounded-md border border-border p-3 text-sm">
                    <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                      <span>{event.eventType}</span>
                      <span>{formatDate(event.createdAt)}</span>
                    </div>
                    {event.message && <div className="mt-1">{event.message}</div>}
                  </div>
                ))}
                {data.events.length === 0 && (
                  <div className="text-sm text-muted-foreground">История пуста</div>
                )}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export function ActionItemsPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'open' | 'OVERDUE' | 'RESOLVED'>('open');
  const [search, setSearch] = useState('');
  const [sourceModule] = useState(searchParams.get('sourceModule') || '');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const params = useMemo(
    () => ({
      status,
      search: search || undefined,
      sourceModule: sourceModule || undefined,
      limit: 80,
    }),
    [status, search, sourceModule],
  );

  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ['mobile-action-items', params],
    queryFn: () => actionItemsApi.list(params),
  });

  const openItem = (item: ActionItemDto) => {
    setSelectedId(item.id);
    setSheetOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        title="Требует внимания"
        showBack
        backTo="/more"
        rightContent={
          <Button variant="ghost" size="icon" onClick={() => refetch()} disabled={isRefetching}>
            {isRefetching ? <Loader2 className="h-5 w-5 animate-spin" /> : <RefreshCw className="h-5 w-5" />}
          </Button>
        }
      />

      <main className="space-y-4 p-4 pb-24">
        <div className="grid grid-cols-3 gap-2 text-center text-sm">
          <div className="rounded-md border border-border bg-card p-2">
            <div className="font-semibold">{data?.summary.active ?? 0}</div>
            <div className="text-xs text-muted-foreground">активные</div>
          </div>
          <div className="rounded-md border border-border bg-card p-2">
            <div className="font-semibold">{data?.summary.overdue ?? 0}</div>
            <div className="text-xs text-muted-foreground">просрочены</div>
          </div>
          <div className="rounded-md border border-border bg-card p-2">
            <div className="font-semibold">{data?.summary.critical ?? 0}</div>
            <div className="text-xs text-muted-foreground">критичные</div>
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-1">
          {[
            ['open', 'Открытые'],
            ['OVERDUE', 'Просроченные'],
            ['RESOLVED', 'Решённые'],
          ].map(([value, label]) => (
            <Button
              key={value}
              size="sm"
              variant={status === value ? 'default' : 'outline'}
              onClick={() => setStatus(value as typeof status)}
            >
              {label}
            </Button>
          ))}
        </div>

        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Поиск"
        />

        <div className="space-y-3">
          {data?.items.map((item) => (
            <ActionItemCard key={item.id} item={item} onOpen={openItem} />
          ))}
          {isLoading && (
            <div className="py-10 text-center">
              <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
          {!isLoading && data?.items.length === 0 && (
            <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              Нет записей
            </div>
          )}
        </div>
      </main>

      <ActionItemSheet
        itemId={selectedId}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
      />
    </div>
  );
}
